import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router';
import styled from 'styled-components';
import { XCircle } from 'lucide-react';

import { useAuthUser } from '@redux/selectors/auth';
import { authSlice } from '@redux/state/authSlice';
import { useAppDispatch } from '@redux/store';
import { useLazyGetUserDataQuery } from '@redux/queries/user';
import { AlertType } from '@utils/constants';
import { getRedirectPath, clearRedirectPath } from '@utils/redirect';
import Loader from '@components/loader';
import * as Styled from '@components/alert/styled';

import { AUTH_ROUTES, APP_ROUTES } from './types';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.black};
  position: relative;
`;

const AlertContainer = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
`;

const AuthCallback = () => {
  const [isShownErrorAlert, setIsShownErrorAlert] = useState<boolean>(false);
  const [getUserData, { isError, isSuccess }] = useLazyGetUserDataQuery();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user: authUser } = useAuthUser();

  const id = searchParams.get('id');
  const token = searchParams.get('token');
  const error = searchParams.get('error');

  useEffect(() => {
    if (id && token && !error) {
      dispatch(authSlice.actions.setUser({ id, token }));
      getUserData();
    }
  }, [dispatch, getUserData, id, token, error]);

  useEffect(() => {
    if (isSuccess && authUser && !authUser?.username) {
      navigate(AUTH_ROUTES.ONBOARDING, { replace: true });
    } else if (authUser && authUser?.emailVerified && authUser?.username) {
      const from =
        (location.state as { from?: string })?.from || getRedirectPath();
      clearRedirectPath();
      navigate(from || APP_ROUTES.HOME, { replace: true });
    }
  }, [isSuccess, authUser, navigate, location.state]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (error || isError) {
      setIsShownErrorAlert(true);

      timeoutId = setTimeout(() => {
        navigate(AUTH_ROUTES.LOGIN, { replace: true });
      }, 3000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [error, isError, navigate]);

  return (
    <Container>
      {isShownErrorAlert && (
        <AlertContainer>
          <Styled.Container
            $variant={AlertType.Error}
            $isExiting={false}
            onClick={() => navigate(AUTH_ROUTES.LOGIN, { replace: true })}
            role='alert'
          >
            <Styled.IconWrapper $variant={AlertType.Error}>
              <XCircle size={16} />
            </Styled.IconWrapper>
            <Styled.Message>
              Authentication failed. Please try again.
            </Styled.Message>
          </Styled.Container>
        </AlertContainer>
      )}

      <Loader size='large' />
    </Container>
  );
};

export default AuthCallback;
